import React, { useState, useEffect, useRef } from 'react';
import { 
  getProducts, 
  deleteProduct, 
  updateProduct,
  reviewProduct,
  verifyProduct,
  publishProduct,
  updateProductStock,
  updateProductImage,
  deleteProductImage,
  downloadProductsTemplate,
  exportProducts,
  importProducts,
  toggleProductFeatured,
  getCultures
} from '../api/api';
import { Link } from 'react-router-dom';
import Comments from '../components/Comments';
import './AdminProducts.css';

const AVAILABLE_CATEGORIES = [
  'Traditional Attire',
  'Accessories',
  'Jewelry',
  'Headwear',
  'Footwear',
  'Home Décor',
  'Ceremonial Items',
  'Traditional Art',
  'Paintings & Drawings',
  'Sculptures & Carvings',
  'Beadwork',
  'Pottery & Ceramics',
  'Textiles & Fabrics',
  'Musical Instruments',
  'Cultural Books',
  'Educational Materials',
  'Traditional Weapons & Tools',
  'Handcrafted Items',
  'Wall Art & Prints'
];

function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingStock, setEditingStock] = useState(null);
  const [stockValue, setStockValue] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [viewingProduct, setViewingProduct] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const fileInputRef = useRef(null);
  const importFileRef = useRef(null);

  const [editForm, setEditForm] = useState({
    name: '',
    price: '',
    description: '',
    category: '',
    culture: '',
    colors: '',
    salePercentage: ''
  });

  const [cultures, setCultures] = useState([]);

  useEffect(() => {
    fetchProducts();
    fetchCultures();
    setSelectedIds([]);
    // eslint-disable-next-line
  }, [filter]);

  const fetchCultures = async () => {
    try {
      const response = await getCultures();
      const culturesArray = Array.isArray(response.data) 
        ? response.data 
        : (response.data.cultures || []);
      setCultures(culturesArray);
    } catch (error) {
      console.error('Error fetching cultures:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {
        limit: 10000, // Fetch all products for admin
        admin: true // See every product regardless of workflow status
      };
      
      if (filter === 'reviewed') {
        params.reviewed = true;
      } else if (filter === 'not-reviewed') {
        params.reviewed = false;
      } else if (filter === 'published') {
        params.isPublished = true;
      } else if (filter === 'hidden') {
        params.isPublished = false;
      }

      const response = await getProducts(params);
      const fetched = response.data.products || response.data || [];
      setProducts(fetched);
      setError('');
      return fetched;
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Failed to fetch products: ' + (error.response?.data?.message || error.message));
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteProduct(id);
      alert('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      alert('Error deleting product: ' + (error.response?.data?.message || error.message));
      console.error(error);
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev => prev.length === products.length ? [] : products.map(p => p._id));
  };

  const runBulkAction = async (label, actionFn, verify) => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`${label} ${selectedIds.length} selected product(s)?`)) return;

    setBulkActionLoading(true);
    try {
      const results = await Promise.allSettled(selectedIds.map(id => actionFn(id)));
      const failedIds = selectedIds.filter((id, i) => results[i].status === 'rejected');

      // Re-fetch and confirm the change actually took effect — never trust a 200
      // response alone, since a request can succeed while the underlying data
      // still doesn't reflect the intended state.
      const freshProducts = await fetchProducts();
      let unverifiedIds = [];
      if (verify) {
        unverifiedIds = selectedIds.filter(id => {
          if (failedIds.includes(id)) return false; // already counted as a failure
          const fresh = freshProducts.find(p => p._id === id);
          return !verify(fresh);
        });
      }

      const problemCount = failedIds.length + unverifiedIds.length;
      if (problemCount > 0) {
        alert(`${label}: ${selectedIds.length - problemCount} of ${selectedIds.length} confirmed successful. ${problemCount} did not apply — please retry those.`);
      } else {
        alert(`${label} confirmed for all ${selectedIds.length} selected product(s)`);
      }
      setSelectedIds(problemCount > 0 ? [...failedIds, ...unverifiedIds] : []);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkReview = () => runBulkAction('Mark reviewed', reviewProduct, (p) => !!p && !!p.reviewed);
  const handleBulkVerify = () => runBulkAction('Mark verified', verifyProduct, (p) => !!p && !!p.verified);
  const handleBulkPublish = () => runBulkAction('Publish', (id) => publishProduct(id, true), (p) => !!p && p.isPublished === true);
  const handleBulkHide = () => runBulkAction('Hide', (id) => publishProduct(id, false), (p) => !!p && p.isPublished === false);
  const handleBulkDelete = () => runBulkAction('Delete', deleteProduct, (p) => !p);

  const handleReview = async (id) => {
    try {
      await reviewProduct(id);
      alert('Product marked as reviewed');
      fetchProducts();
    } catch (error) {
      alert('Error reviewing product: ' + (error.response?.data?.message || error.message));
      console.error(error);
    }
  };

  const handleVerify = async (id) => {
    try {
      await verifyProduct(id);
      alert('Product marked as verified');
      fetchProducts();
    } catch (error) {
      alert('Error verifying product: ' + (error.response?.data?.message || error.message));
      console.error(error);
    }
  };

  const handlePublishToggle = async (id) => {
    try {
      const product = products.find(p => p._id === id);
      if (!product) return;
      
      // Toggle the publish state
      const newPublishState = !product.isPublished;
      const response = await publishProduct(id, newPublishState);
      alert(response.data.message);
      fetchProducts();
    } catch (error) {
      alert('Error toggling publish status: ' + (error.response?.data?.message || error.message));
      console.error(error);
    }
  };

  const handleToggleFeatured = async (id) => {
    try {
      await toggleProductFeatured(id);
      alert('Featured status updated');
      fetchProducts();
    } catch (error) {
      alert('Error updating featured status');
      console.error(error);
    }
  };

  const startEditingStock = (product) => {
    setEditingStock(product._id);
    setStockValue(product.stockQuantity || 0);
  };

  const handleStockUpdate = async (id) => {
    try {
      await updateProductStock(id, parseInt(stockValue));
      alert('Stock updated successfully');
      setEditingStock(null);
      fetchProducts();
    } catch (error) {
      alert('Error updating stock: ' + (error.response?.data?.message || error.message));
      console.error(error);
    }
  };

  const handleImageClick = (product) => {
    setImagePreview(product);
    fileInputRef.current?.click();
  };

  const handleImageUpload = async (e, productId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate image
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file');
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert('Image size should not exceed 5MB');
      return;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = async () => {
      if (img.width < 400 || img.height < 400) {
        alert('Image dimensions should be at least 400x400 pixels');
        return;
      }

      const formData = new FormData();
      formData.append('image', file);

      try {
        await updateProductImage(productId, formData);
        alert('Image updated successfully');
        setImagePreview(null);
        fetchProducts();
      } catch (error) {
        alert('Error updating image: ' + (error.response?.data?.message || error.message));
        console.error(error);
      }
    };
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadProductsTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'products-template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Error downloading template: ' + (error.response?.data?.message || error.message));
      console.error(error);
    }
  };

  const handleExportProducts = async () => {
    try {
      const response = await exportProducts();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `products-export-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Error exporting products: ' + (error.response?.data?.message || error.message));
      console.error(error);
    }
  };

  const handleImportProducts = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      alert('Please select an Excel file (.xlsx or .xls)');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await importProducts(formData);
      alert(response.data.message);
      if (response.data.errors && response.data.errors.length > 0) {
        console.warn('Import errors:', response.data.errors);
      }
      fetchProducts();
    } catch (error) {
      alert('Error importing products: ' + (error.response?.data?.message || error.message));
      console.error(error);
    }

    // Reset file input
    if (importFileRef.current) {
      importFileRef.current.value = '';
    }
  };

  const startEditing = (product) => {
    setEditingProduct(product._id);
    setEditForm({
      name: product.name,
      price: product.price,
      description: product.description,
      category: product.category,
      culture: product.culture || '',
      colors: Array.isArray(product.colors) ? product.colors.join(', ') : '',
      salePercentage: product.salePercentage || ''
    });
  };

  const handleEditChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const handleEditSubmit = async (id) => {
    try {
      const formData = new FormData();
      Object.keys(editForm).forEach(key => {
        formData.append(key, editForm[key]);
      });

      await updateProduct(id, formData);
      alert('Product updated successfully');
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      alert('Error updating product');
      console.error(error);
    }
  };

  const toggleExpanded = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleViewImage = (image, product) => {
    setSelectedImage({ ...image, productName: product.name, productId: product._id });
    setShowImageViewer(true);
  };

  const handleViewAllImages = (product) => {
    setViewingProduct(product);
  };

  const handleDeleteImage = async (productId, imageIndex) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }
    try {
      await deleteProductImage(productId, imageIndex);
      alert('Image deleted successfully');
      await fetchProducts();
      // Keep the open gallery modal in sync so indices don't go stale after a delete
      setViewingProduct(prev => {
        if (!prev || prev._id !== productId || !prev.images) return prev;
        return { ...prev, images: prev.images.filter((_, idx) => idx !== Number(imageIndex)) };
      });
      if (selectedImage && selectedImage.productId === productId) {
        setShowImageViewer(false);
        setSelectedImage(null);
      }
    } catch (error) {
      alert('Error deleting image: ' + error.message);
      console.error(error);
    }
  };

  const handleAddImage = async (productId) => {
    const file = await new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = (e) => resolve(e.target.files[0]);
      input.click();
    });

    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('alt', 'Product image');
      await updateProductImage(productId, formData);
      alert('Image added successfully');
      fetchProducts();
    } catch (error) {
      alert('Error adding image: ' + error.message);
      console.error(error);
    }
  };

  const getWorkflowStatus = (product) => {
    if (!product.reviewed) return { text: 'Needs Review', color: '#f44336' };
    if (!product.verified) return { text: 'Needs Verification', color: '#ff9800' };
    if (!product.isPublished) return { text: 'Ready to Publish', color: '#2196F3' };
    return { text: 'Published', color: '#4CAF50' };
  };

  if (loading) {
    return <div className="loading">Loading products...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="admin-products">
      <div className="admin-header">
        <h2>Product Management</h2>
        <div className="product-count-badge">
          Total: {products.length} product{products.length !== 1 ? 's' : ''}
        </div>
        <div className="admin-header-actions">
          <Link to="/admin/dashboard/products/new" className="btn-add">
            + Add New Product
          </Link>
          <button onClick={handleDownloadTemplate} className="btn-secondary">
            Download Template
          </button>
          <button onClick={handleExportProducts} className="btn-secondary">
            Export All Products
          </button>
          <label className="btn-secondary" style={{cursor: 'pointer'}}>
            Import Products
            <input 
              type="file" 
              ref={importFileRef}
              accept=".xlsx,.xls"
              onChange={handleImportProducts}
              style={{display: 'none'}}
            />
          </label>
        </div>
      </div>

      <div className="filters">
        <button 
          className={filter === 'all' ? 'active' : ''} 
          onClick={() => setFilter('all')}
        >
          All Products ({products.length})
        </button>
        <button 
          className={filter === 'reviewed' ? 'active' : ''} 
          onClick={() => setFilter('reviewed')}
        >
          Reviewed
        </button>
        <button 
          className={filter === 'not-reviewed' ? 'active' : ''} 
          onClick={() => setFilter('not-reviewed')}
        >
          Not Reviewed
        </button>
        <button 
          className={filter === 'published' ? 'active' : ''} 
          onClick={() => setFilter('published')}
        >
          Published
        </button>
        <button 
          className={filter === 'hidden' ? 'active' : ''} 
          onClick={() => setFilter('hidden')}
        >
          Hidden
        </button>
      </div>

      {selectedIds.length > 0 && (
        <div className="bulk-actions-bar">
          <span>{selectedIds.length} selected</span>
          <button disabled={bulkActionLoading} onClick={handleBulkReview} className="btn-sm btn-primary">Mark Reviewed</button>
          <button disabled={bulkActionLoading} onClick={handleBulkVerify} className="btn-sm btn-success">Mark Verified</button>
          <button disabled={bulkActionLoading} onClick={handleBulkPublish} className="btn-sm btn-published">Publish</button>
          <button disabled={bulkActionLoading} onClick={handleBulkHide} className="btn-sm btn-hidden">Hide</button>
          <button disabled={bulkActionLoading} onClick={handleBulkDelete} className="btn-sm btn-danger">Delete</button>
          <button disabled={bulkActionLoading} onClick={() => setSelectedIds([])} className="btn-sm">Clear Selection</button>
        </div>
      )}

      {products.length === 0 ? (
        <div className="no-products">
          <p>No products found</p>
        </div>
      ) : (
        <div className="products-table-container">
          <table className="products-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={products.length > 0 && selectedIds.length === products.length}
                    onChange={toggleSelectAll}
                    title="Select all"
                  />
                </th>
                <th>Image</th>
                <th>Name</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Workflow Status</th>
                <th>Featured</th>
                <th>Publish Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <React.Fragment key={product._id}>
                  <tr>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(product._id)}
                        onChange={() => toggleSelected(product._id)}
                      />
                    </td>
                    <td>
                      <div className="product-image-cell">
                        {product.images && product.images.length > 0 ? (
                          <div className="image-actions">
                            <img 
                              src={product.images[0].url || product.images[0]} 
                              alt={product.name}
                              style={{width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer'}}
                              onClick={() => handleViewAllImages(product)}
                              title={`Click to view all ${product.images.length} image(s)`}
                            />
                            <div className="image-count">{product.images.length}</div>
                            <button 
                              className="btn-add-image" 
                              onClick={() => handleAddImage(product._id)}
                              title="Add image"
                            >
                              +
                            </button>
                          </div>
                        ) : (
                          <div style={{width: '60px', height: '60px', background: '#f0f0f0', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'}} 
                               onClick={() => handleAddImage(product._id)}
                               title="Click to add image"
                          >
                            No Image
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      {editingProduct === product._id ? (
                        <input
                          type="text"
                          name="name"
                          value={editForm.name}
                          onChange={handleEditChange}
                          className="edit-input"
                        />
                      ) : (
                        <strong>{product.name}</strong>
                      )}
                      <div style={{fontSize: '0.9em', color: '#666'}}>
                        {product.culture} - {product.category}
                      </div>
                      {product.sku && (
                        <div style={{fontSize: '0.8em', color: '#999'}}>SKU: {product.sku}</div>
                      )}
                    </td>
                    <td>
                      {editingProduct === product._id ? (
                        <input
                          type="number"
                          name="price"
                          value={editForm.price}
                          onChange={handleEditChange}
                          className="edit-input"
                          style={{width: '100px'}}
                        />
                      ) : product.salePercentage > 0 ? (
                        <div>
                          <span style={{textDecoration: 'line-through', color: '#999', marginRight: '6px'}}>R {product.price}</span>
                          <span style={{color: '#c0392b', fontWeight: 'bold'}}>R {(product.salePrice ?? product.price * (1 - product.salePercentage / 100)).toFixed(2)}</span>
                          <div className="sale-tag">SALE {product.salePercentage}% OFF</div>
                        </div>
                      ) : (
                        `R ${product.price}`
                      )}
                    </td>
                    <td>
                      {editingStock === product._id ? (
                        <div style={{display: 'flex', gap: '5px', alignItems: 'center'}}>
                          <input
                            type="number"
                            value={stockValue}
                            onChange={(e) => setStockValue(e.target.value)}
                            className="edit-input"
                            style={{width: '70px'}}
                            min="0"
                          />
                          <button onClick={() => handleStockUpdate(product._id)} className="btn-sm btn-success">
                            Save
                          </button>
                          <button onClick={() => setEditingStock(null)} className="btn-sm">
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={{display: 'flex', gap: '5px', alignItems: 'center'}}>
                          <span>{product.stock || product.stockQuantity || 0}</span>
                          {product.stockStatus === 'saved' && (
                            <span style={{fontSize: '0.8em', color: '#4CAF50'}}>✓ Saved</span>
                          )}
                          <button 
                            onClick={() => startEditingStock(product)} 
                            className="btn-sm"
                            disabled={product.stockStatus === 'saved' && editingStock !== product._id}
                            style={{opacity: product.stockStatus === 'saved' && editingStock !== product._id ? 0.5 : 1}}
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </td>
                    <td>
                      <span 
                        className="workflow-badge"
                        style={{
                          backgroundColor: getWorkflowStatus(product).color,
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.85em',
                          display: 'inline-block'
                        }}
                      >
                        {getWorkflowStatus(product).text}
                      </span>
                      <div style={{fontSize: '0.8em', marginTop: '4px'}}>
                        {product.reviewedBy && (
                          <div>✓ Reviewed by {product.reviewedBy.email}</div>
                        )}
                        {product.verifiedBy && (
                          <div>✓ Verified by {product.verifiedBy.email}</div>
                        )}
                        {product.publishedBy && (
                          <div>
                            {product.isPublished ? '👁️' : '🙈'} by {product.publishedBy.email}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <button 
                        onClick={() => handleToggleFeatured(product._id)}
                        className={`btn-sm ${product.isFeatured ? 'btn-featured' : ''}`}
                        title={product.isFeatured ? 'Seasonal/Limited Time' : 'Mark as Featured'}
                      >
                        {product.isFeatured ? '⭐ Featured' : 'Mark Featured'}
                      </button>
                    </td>
                    <td>
                      {product.reviewed && product.verified ? (
                        <button 
                          onClick={() => handlePublishToggle(product._id)}
                          className={`btn-sm ${product.isPublished ? 'btn-published' : 'btn-hidden'}`}
                          title={product.isPublished ? 'Click to hide product' : 'Click to publish product'}
                        >
                          {product.isPublished ? '👁️ Published' : '🙈 Hidden'}
                        </button>
                      ) : (
                        <span style={{fontSize: '0.9em', color: '#999'}}>
                          {!product.reviewed ? 'Needs Review' : 'Needs Verification'}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        {!product.reviewed && (
                          <button onClick={() => handleReview(product._id)} className="btn-sm btn-primary">
                            Mark Reviewed
                          </button>
                        )}
                        {product.reviewed && !product.verified && (
                          <button onClick={() => handleVerify(product._id)} className="btn-sm btn-success">
                            Mark Verified
                          </button>
                        )}
                        {editingProduct === product._id ? (
                          <>
                            <button onClick={() => handleEditSubmit(product._id)} className="btn-sm btn-success">
                              Save
                            </button>
                            <button onClick={() => setEditingProduct(null)} className="btn-sm">
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => startEditing(product)} className="btn-sm">
                              Edit
                            </button>
                            <button onClick={() => toggleExpanded(product._id)} className="btn-sm">
                              {expandedId === product._id ? 'Hide' : 'Show'} Comments
                            </button>
                            <button 
                              onClick={() => handleDelete(product._id, product.name)} 
                              className="btn-sm btn-danger"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  {editingProduct === product._id && (
                    <tr className="expanded-row edit-form-row">
                      <td colSpan="9">
                        <div className="expanded-content">
                          <h4>✏️ Edit Product Details</h4>
                          <div className="edit-form">
                            <div className="form-grid">
                              <div className="form-group">
                                <label htmlFor={`name-${product._id}`}>Product Name</label>
                                <input
                                  id={`name-${product._id}`}
                                  type="text"
                                  name="name"
                                  value={editForm.name}
                                  onChange={handleEditChange}
                                  className="edit-input"
                                />
                              </div>
                              <div className="form-group">
                                <label htmlFor={`price-${product._id}`}>Price (R)</label>
                                <input
                                  id={`price-${product._id}`}
                                  type="number"
                                  name="price"
                                  value={editForm.price}
                                  onChange={handleEditChange}
                                  className="edit-input"
                                  step="0.01"
                                />
                              </div>
                              <div className="form-group">
                                <label htmlFor={`category-${product._id}`}>Category</label>
                                <select
                                  id={`category-${product._id}`}
                                  name="category"
                                  value={editForm.category}
                                  onChange={handleEditChange}
                                  className="edit-input"
                                >
                                  <option value="">Select a category</option>
                                  {AVAILABLE_CATEGORIES.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="form-group">
                                <label htmlFor={`culture-${product._id}`}>Culture</label>
                                <select
                                  id={`culture-${product._id}`}
                                  name="culture"
                                  value={editForm.culture}
                                  onChange={handleEditChange}
                                  className="edit-input"
                                >
                                  <option value="">Select a culture</option>
                                  {cultures.map(cult => (
                                    <option key={cult._id || cult.name} value={cult.name}>{cult.name}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="form-group full-width">
                                <label htmlFor={`description-${product._id}`}>Description</label>
                                <textarea
                                  id={`description-${product._id}`}
                                  name="description"
                                  value={editForm.description}
                                  onChange={handleEditChange}
                                  className="edit-input"
                                  rows="5"
                                  placeholder="Enter product description..."
                                />
                              </div>
                              <div className="form-group full-width">
                                <label htmlFor={`colors-${product._id}`}>Colour Options</label>
                                <input
                                  id={`colors-${product._id}`}
                                  type="text"
                                  name="colors"
                                  value={editForm.colors}
                                  onChange={handleEditChange}
                                  className="edit-input"
                                  placeholder="e.g. Red, Blue, Green (leave empty for no colour options / default)"
                                />
                                <small>Separate multiple colours with a comma. Leave empty if this product has no colour options.</small>
                              </div>
                              <div className="form-group full-width">
                                <label htmlFor={`sale-${product._id}`}>Sale % Off</label>
                                <input
                                  id={`sale-${product._id}`}
                                  type="number"
                                  name="salePercentage"
                                  value={editForm.salePercentage}
                                  onChange={handleEditChange}
                                  className="edit-input"
                                  min="0"
                                  max="100"
                                  step="1"
                                  placeholder="e.g. 50 (leave empty for no sale)"
                                />
                                <small>Leave empty for no sale. Otherwise a SALE tag shows and the product appears in Featured Products.</small>
                                {editForm.price && editForm.salePercentage > 0 && editForm.salePercentage <= 100 && (
                                  <p className="sale-preview">
                                    <span className="sale-preview__original">R{parseFloat(editForm.price).toFixed(2)}</span>
                                    {' '}
                                    <span className="sale-preview__sale">
                                      R{(parseFloat(editForm.price) * (1 - editForm.salePercentage / 100)).toFixed(2)}
                                    </span>
                                    {' '}
                                    <span className="sale-preview__pct">({editForm.salePercentage}% OFF)</span>
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="form-actions">
                              <button onClick={() => handleEditSubmit(product._id)} className="btn-save">
                                💾 Save Changes
                              </button>
                              <button onClick={() => setEditingProduct(null)} className="btn-cancel">
                                ✕ Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {expandedId === product._id && (
                    <tr className="expanded-row">
                      <td colSpan="9">
                        <div className="expanded-content">
                          <h4>Product Notes & Comments</h4>
                          <Comments 
                            entityType="product"
                            entityId={product._id}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Hidden file input for image upload */}
      <input 
        type="file" 
        ref={fileInputRef}
        accept="image/*"
        onChange={(e) => imagePreview && handleImageUpload(e, imagePreview._id)}
        style={{display: 'none'}}
      />

      {/* All Images Viewer Modal */}
      {viewingProduct && (
        <div className="modal-overlay" onClick={() => setViewingProduct(null)}>
          <div className="modal-content image-gallery-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🖼️ All Images - {viewingProduct.name}</h2>
              <button className="close-btn" onClick={() => setViewingProduct(null)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="image-grid">
                {viewingProduct.images && viewingProduct.images.length > 0 ? (
                  viewingProduct.images.map((img, idx) => (
                    <div key={idx} className="image-grid-item">
                      <img 
                        src={img.url || img} 
                        alt={img.alt || `Image ${idx + 1}`}
                        onClick={() => handleViewImage(img.url ? img : {url: img, alt: viewingProduct.name}, viewingProduct)}
                        style={{cursor: 'pointer'}}
                        title="Click to view details"
                      />
                      <div className="image-grid-actions">
                        <button 
                          className="btn-sm btn-danger"
                          onClick={() => handleDeleteImage(viewingProduct._id, idx)}
                          title="Delete this image"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                      <div className="image-grid-info">
                        <small>Image {idx + 1} of {viewingProduct.images.length}</small>
                      </div>
                    </div>
                  ))
                ) : (
                  <p>No images found</p>
                )}
              </div>
              
              <div style={{marginTop: '20px', textAlign: 'center'}}>
                <button 
                  className="btn btn-primary"
                  onClick={() => handleAddImage(viewingProduct._id)}
                >
                  ➕ Add New Image
                </button>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setViewingProduct(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {showImageViewer && selectedImage && (
        <div className="modal-overlay" onClick={() => setShowImageViewer(false)}>
          <div className="modal-content image-viewer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📷 Image Viewer - {selectedImage.productName}</h2>
              <button className="close-btn" onClick={() => setShowImageViewer(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="image-display">
                <img src={selectedImage.url} alt={selectedImage.alt || 'Product image'} />
              </div>
              
              <div className="image-properties">
                <h3>Image Properties</h3>
                <table>
                  <tbody>
                    <tr>
                      <td><strong>URL:</strong></td>
                      <td style={{wordBreak: 'break-all'}}>
                        <a href={selectedImage.url} target="_blank" rel="noopener noreferrer">
                          {selectedImage.url}
                        </a>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Alt Text:</strong></td>
                      <td>{selectedImage.alt || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>Public ID:</strong></td>
                      <td>{selectedImage.publicId || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>Type:</strong></td>
                      <td>{selectedImage.url.match(/\.(jpg|jpeg|png|gif|webp|svg)/i)?.[1]?.toUpperCase() || 'Unknown'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowImageViewer(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminProducts;
